import { PayloadAction } from '@reduxjs/toolkit';
import { QueryClient } from '@tanstack/react-query';
import { message } from 'antd';
import { put, takeLatest, select, call } from 'redux-saga/effects';
import { ProjectId, TaskId } from 'api/models';
import { TaskAPI, TaskSourceType, Task as TaskApi } from 'api/tasks';
import { getQueryClientContext } from 'data/core/sagas/query-client';
import { removeTask } from 'data/home/slice';
import { cleanupTaskDependenciesAndRemoveFromTimeline } from 'data/projects/sagas/project/timeline';
import { removeTaskFromSpaceTab } from 'data/space-tabs';
import {
  getProjectDependencyMapKey,
  getProjectTasksQueryKey,
} from 'queries/common';
import { DeleteTaskActionPayload, deleteTaskSagaAction } from '../actions';
import { Task } from '../schema';
import { selectTask } from '../selectors';
import { deleteTask } from '../slice';

function cleanupSuccessorDependenciesInQueryCache(
  deletedTaskId: number,
  queryClient: QueryClient,
  projectId: number,
): void {
  // Null safety checks
  if (!queryClient || !deletedTaskId || !projectId) {
    return;
  }

  // Update tasks in query cache to remove dependencies on the deleted task
  // AND remove the deleted task itself from the cache
  queryClient.setQueryData<TaskApi[]>(
    getProjectTasksQueryKey(ProjectId(projectId)),
    (tasks: TaskApi[] | undefined) => {
      if (!tasks || !Array.isArray(tasks)) {
        return tasks;
      }

      return tasks
        .filter((task) => task && task.taskId !== deletedTaskId) // Remove the deleted task
        .map((task) => {
          if (!task) {
            return task;
          }

          // Clean up dependentTasks array - remove references to the deleted task
          const updatedDependentTasks = task.dependentTasks
            ? task.dependentTasks.filter(
                (dep) => dep && dep.taskId !== deletedTaskId,
              )
            : [];

          // Clean up subTasks array if this task has subtasks
          const updatedSubTasks = task.subTasks
            ? task.subTasks.filter(
                (subtask) => subtask && subtask.taskId !== deletedTaskId,
              )
            : [];

          // Clean up subTaskOrder array
          const updatedSubTaskOrder = task.subTaskOrder
            ? task.subTaskOrder.filter((id) => id !== deletedTaskId)
            : [];

          return {
            ...task,
            dependentTasks: updatedDependentTasks,
            subTasks: updatedSubTasks,
            subTaskOrder: updatedSubTaskOrder,
          };
        });
    },
  );
}

function* deleteTaskByIdSaga({
  payload,
}: PayloadAction<DeleteTaskActionPayload>): Generator {
  const { projectId, taskId, options } = payload;
  const task = (yield select(selectTask(TaskId(projectId, taskId)))) as Task;
  const queryClient = (yield getQueryClientContext()) as QueryClient;

  // 1) Clean Bryntum first (atomic + committed)
  yield call(cleanupTaskDependenciesAndRemoveFromTimeline, taskId);

  // 2) Immediately clean React Query cache to prevent stale references
  yield call(
    cleanupSuccessorDependenciesInQueryCache,
    taskId,
    queryClient,
    projectId,
  );

  // 3) Now do Redux/UI changes (safe, engine and cache are consistent)
  if (
    task?.source?.sourceId &&
    task.source.sourceType === TaskSourceType.SpaceTab
  ) {
    yield put(
      removeTaskFromSpaceTab({
        spaceTabId: task.source.sourceId,
        taskId,
      }),
    );
  }
  yield put(removeTask(taskId));
  if (!options?.skipStoreUpdate) {
    yield put(deleteTask(TaskId(projectId, taskId)));
  }

  // 4) Server + cache
  try {
    yield TaskAPI.deleteTask(projectId, taskId, options?.actor);

    if (!options?.skipRefetch) {
      yield queryClient.refetchQueries(
        getProjectTasksQueryKey(ProjectId(projectId)),
      );
    } else {
      queryClient.setQueryData<TaskApi[]>(
        getProjectTasksQueryKey(ProjectId(Number(projectId))),
        (tasks) => tasks?.filter((t) => t.taskId !== taskId) ?? [],
      );
    }

    yield queryClient.invalidateQueries(getProjectDependencyMapKey(projectId));
  } catch (error) {
    message.error('There was an error processing your request.', error as any);
  }
}

export const watchDeleteTaskByIdSaga = function* (): Generator {
  yield takeLatest(deleteTaskSagaAction.type, deleteTaskByIdSaga);
};
